import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Globe } from "lucide-react";
import { LogoIcon, LogoText } from "./LogoIcon";
import { useLang } from "@/context/LanguageContext";

const Section = ({ title, content, email }) => (
  <div className="mb-10">
    <h2 className="text-lg font-bold text-brand-orange uppercase tracking-wider mb-4">{title}</h2>
    <div className="space-y-3 text-white/60 text-sm leading-relaxed">
      {content.map((paragraph, i) =>
        paragraph.startsWith("•") ? (
          <ul key={i} className="space-y-1 pl-2">
            {paragraph.split("\n").map((line, j) => (
              <li key={j} className="flex gap-2">
                <span className="text-brand-cyan shrink-0">•</span>
                <span>{line.replace("• ", "")}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p key={i}>{paragraph}</p>
        )
      )}
      {email && <p className="text-brand-cyan font-medium">{email}</p>}
    </div>
  </div>
);

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLang();
  const terms = t.terms;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            {terms.back}
          </button>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white border border-white/10 hover:border-brand-cyan/50 rounded transition-all duration-300"
            >
              <Globe size={14} />
              {lang === "es" ? "EN" : "ES"}
            </button>
            <div className="flex items-center gap-2.5">
              <LogoIcon size={28} />
              <LogoText height={20} />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-16">
        <div className="mb-12">
          <p className="text-xs text-brand-cyan uppercase tracking-widest mb-3 font-bold">{terms.badge}</p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{terms.title}</h1>
          <p className="text-white/40 text-sm">{terms.updated}</p>
        </div>

        {terms.sections.map((section, i) => (
          <Section key={i} title={section.title} content={section.content} email={section.email} />
        ))}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-4 sm:px-8 py-6">
        <p className="text-center text-white/25 text-xs">
          &copy; {new Date().getFullYear()} Realm Pixel Games. {t.footer.rights}
        </p>
      </footer>
    </div>
  );
}
